'use client';

import type { PostPaginationProps } from '@routeroyal/theme-core';
import { motion } from 'framer-motion';

/**
 * Stone Investment Post Pagination
 * 
 * Minimal, editorial-style pagination with:
 * - Simple arrow navigation
 * - Refined typography
 * - Elegant animations
 */
interface StonePostPaginationProps extends PostPaginationProps {
  messages?: {
    pagination?: string;
    previousPage?: string;
    prev?: string;
    nextPage?: string;
    next?: string;
  };
}

export function StoneInvestmentPostPagination({
  currentPage,
  totalPages,
  onPageChange,
  className = '',
  messages: customMessages = {},
}: StonePostPaginationProps) {
  const labels = {
    pagination: 'Pagination',
    previousPage: 'Previous page',
    prev: 'Prev',
    nextPage: 'Next page',
    next: 'Next',
    ...customMessages,
  };
  if (totalPages <= 1) {
    return null;
  }

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages: (number | 'ellipsis')[] = [];
    const showEllipsis = totalPages > 7;

    if (!showEllipsis) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      if (currentPage > 3) {
        pages.push('ellipsis');
      }

      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
        pages.push(i);
      }

      if (currentPage < totalPages - 2) {
        pages.push('ellipsis');
      }

      pages.push(totalPages);
    }

    return pages;
  };

  const pageNumbers = getPageNumbers();

  return (
    <nav
      className={`flex items-center justify-center gap-1 ${className}`}
      aria-label={labels.pagination}
      style={{ fontFamily: "'DM Sans', sans-serif" }}
    >
      {/* Previous Button */}
      <motion.button
        whileHover={{ x: -2 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage <= 1}
        className={`
          flex items-center gap-2 px-4 py-2
          text-xs tracking-[0.2em] uppercase
          transition-all duration-300
          ${currentPage <= 1
            ? 'text-[var(--color-border)] cursor-not-allowed'
            : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
          }
        `}
        aria-label={labels.previousPage}
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19l-7-7 7-7" />
        </svg>
        <span className="hidden sm:inline">{labels.prev}</span>
      </motion.button>

      {/* Page Numbers */}
      <div className="flex items-center gap-1 mx-4">
        {pageNumbers.map((page, index) =>
          page === 'ellipsis' ? (
            <span
              key={`ellipsis-${index}`}
              className="px-2 py-2 text-[var(--color-text-muted)]"
            >
              ···
            </span>
          ) : (
            <motion.button
              key={page}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => onPageChange(page)}
              className={`
                w-10 h-10 text-sm
                transition-all duration-300
                ${currentPage === page
                  ? 'bg-[var(--color-text)] text-[var(--color-background)]'
                  : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
                }
              `}
              aria-label={`Page ${page}`}
              aria-current={currentPage === page ? 'page' : undefined}
            >
              {page}
            </motion.button>
          )
        )}
      </div>

      {/* Next Button */}
      <motion.button
        whileHover={{ x: 2 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage >= totalPages}
        className={`
          flex items-center gap-2 px-4 py-2
          text-xs tracking-[0.2em] uppercase
          transition-all duration-300
          ${currentPage >= totalPages
            ? 'text-[var(--color-border)] cursor-not-allowed'
            : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
          }
        `}
        aria-label={labels.nextPage}
      >
        <span className="hidden sm:inline">{labels.next}</span>
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5l7 7-7 7" />
        </svg>
      </motion.button>
    </nav>
  );
}
